import { Editor } from '@monaco-editor/react';
import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { ResultsTable } from '@shadcn/components/results-table';
import { useRunQuery } from '@shadcn/hooks/use-run-query';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Skeleton } from '~/components/ui/Skeleton';
import { themeIsDark } from '~/lib/stores/theme';
import type { PostgresTable } from '@shadcn/lib/pg-meta/types';
import { defineMonacoTheme } from '@shadcn/lib/editor-theme';
import type { SortDir } from '~/components/ui/SortIndicator';

interface SqlEditorProps {
  initialSql?: string;
  hideSql?: boolean;
  defaultLimit?: number;
  defaultSortField?: string;
  defaultSortDirection?: 'ASC' | 'DESC';
  queryKey?: any;
  label?: ReactNode;
  onResults?: (data: any[] | undefined) => void;
  onRowClick?: (row: any, queryKey?: any) => void;
  readOnly?: boolean;
  runAutomatically?: boolean;
  refetch?: number;
  initialNaturalLanguageMode?: boolean;
  includedFields?: string[];
  noResultLabel?: string;
  onSortChange?: (sortField: string, sortDirection: SortDir) => void;
}

export function SqlEditor({
  initialSql,
  onSortChange,
  defaultLimit,
  defaultSortField,
  defaultSortDirection,
  queryKey,
  hideSql = false,
  onResults,
  onRowClick,
  label = 'Query your data',
  readOnly = false,
  runAutomatically = false,
  refetch,
  includedFields,
  noResultLabel,
}: SqlEditorProps) {
  const [sql, setSql] = useState(initialSql || '');

  const { mutate: runQuery, data, isPending, error } = useRunQuery();

  useEffect(() => {
    if (initialSql) {
      setSql(initialSql);
    }
  }, [initialSql]);

  const handleRunQuery = useCallback(() => {
    if (sql) {
      runQuery({ query: sql });
    }
  }, [sql, runQuery]);

  useEffect(() => {
    if (runAutomatically && initialSql) {
      runQuery({ query: initialSql });
    }
  }, [runAutomatically, initialSql, runQuery]);

  useEffect(() => {
    if (refetch && refetch > 0) {
      handleRunQuery();
    }
  }, [refetch, handleRunQuery]);

  useEffect(() => {
    if (onResults) {
      onResults(data);
    }
  }, [data, onResults]);

  const serverErrorMessage = (error as any)?.response?.data?.message || '';
  const isReadOnlyError = serverErrorMessage.includes('permission denied') || serverErrorMessage.includes('42501');
  const customReadOnlyError = "You can't directly alter your database schema, use chat instead";

  return (
    <div>
      <div>
        <div className="flex items-center gap-4 mb-4 mt-4">
          <h2 className="text-base text-bolt-ds-textPrimary flex-1">{label}</h2>
        </div>
        {!hideSql && (
          <div className="mt-4 flex flex-col gap-2">
            <div className="rounded-md border border-bolt-ds-borderOutline overflow-hidden">
              <Editor
                height="75px"
                language="sql"
                value={sql}
                beforeMount={defineMonacoTheme}
                onChange={(value) => setSql(value || '')}
                theme={themeIsDark() ? 'custom-dark' : 'custom-light'}
                className="bg-transparent"
                options={{
                  minimap: { enabled: false },
                  lineNumbers: 'off',
                  language: 'sql',
                  fontSize: 16,
                  readOnly,
                  renderLineHighlight: 'none',
                  padding: {
                    top: 10,
                    bottom: 24,
                  },
                  scrollbar: { vertical: 'hidden', horizontal: 'hidden' },
                }}
              />
            </div>
            <Button size="md" onClick={handleRunQuery} loading={isPending} disabled={isPending} className="ml-auto">
              {isPending ? 'Running...' : 'Run Query'}
            </Button>
          </div>
        )}
      </div>
      {isPending && (
        <div className="space-y-2 mt-4">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}

      {error && (
        <div className="py-4">
          <Alert type="danger" title="Query Error">
            {isReadOnlyError
              ? customReadOnlyError
              : serverErrorMessage || (error as any)?.message || 'An unexpected error occurred'}
          </Alert>
        </div>
      )}

      {data && (
        <div className="mt-4">
          <ResultsTable
            data={data.map((item: PostgresTable) => {
              let filteredItem: any = {};
              if (!includedFields) return item;
              Object.keys(item).forEach((key) => {
                if (includedFields.includes(key)) {
                  filteredItem[key] = item[key as keyof PostgresTable];
                }
              });
              return filteredItem;
            })}
            onSortChange={onSortChange}
            sortField={defaultSortField}
            sortDirection={defaultSortDirection}
            noResultLabel={noResultLabel}
            onRowClick={(row) => onRowClick?.(row, queryKey)}
            isDefaultQuery={initialSql === sql}
            defaultLimit={defaultLimit}
          />
        </div>
      )}
    </div>
  );
}
